import { useQuery } from '@tanstack/react-query';

import { fetchHOAChecklist } from '@/services/api/hoa';
import { HOAChecklistResponse } from '@/types/HOA';
import { logger } from '@/utils/logger/loggerInstance';

export interface UseHOAChecklistResult {
  data: HOAChecklistResponse | null;
  isLoading: boolean;
  /** True on a real fetch failure (403/500/network) — distinct from `data`
   *  being null because the property simply isn't HOA-governed. Callers that
   *  only branch on `data` will render the same "nothing here" state either
   *  way; check this when it matters to tell the two apart. */
  isError: boolean;
}

// Dev-only preview: append ?mock=hoa to any property's Context Score URL to
// see HOAChecklistCard fully populated without a deployed backend — same
// pattern as ?mock=address-mismatch (UploaderProvider) and ?mock=buyer-badge
// (PropertiesPageNew). Never active outside `yarn dev`.
const HOA_MOCK_RESPONSE: HOAChecklistResponse = {
  hasHOA: true,
  hoaChecklist: [
    { id: 'ccrs', label: 'CC&Rs (Covenants, Conditions & Restrictions)', status: 'RECEIVED' },
    { id: 'bylaws', label: 'HOA Bylaws', status: 'NOT_RECEIVED' },
    { id: 'articles-of-incorporation', label: 'Articles of Incorporation', status: 'NOT_RECEIVED' },
    { id: 'rules-regulations', label: 'Rules & Regulations', status: 'RECEIVED' },
    { id: 'board-minutes', label: 'Board Meeting Minutes (last 12 months)', status: 'RECEIVED' },
    { id: 'operating-budget', label: 'Current Operating Budget', status: 'RECEIVED' },
    { id: 'reserve-study', label: 'Reserve Study / Funding Plan', status: 'RECEIVED' },
    {
      id: 'assessment-reserve-disclosure',
      label: 'Assessment & Reserve Funding Disclosure Summary',
      status: 'RECEIVED',
    },
    { id: 'financial-statement', label: 'Financial Statement / Audit Review', status: 'NOT_RECEIVED' },
    { id: 'insurance-summary', label: 'Insurance Coverage Summary', status: 'RECEIVED' },
    {
      id: 'demand-statement',
      label: 'Required Statement of Fees (Demand Statement)',
      status: 'RECEIVED',
    },
    { id: 'litigation-disclosure', label: 'Pending Litigation Disclosure', status: 'RECEIVED' },
    { id: 'violation-notices', label: 'Notice of Violations / Enforcement Actions', status: 'RECEIVED' },
    {
      id: 'assessment-enforcement-notice',
      label: 'Assessment Enforcement / Lien Policy',
      status: 'RECEIVED',
    },
  ],
  hoaFindings: {
    assessmentAmount: 415,
    assessmentFrequency: 'monthly',
    delinquencyStatus: 'current',
    delinquentAmount: null,
    specialAssessments: [],
    violations: [
      {
        description: 'Unauthorized exterior paint color on unit fence',
        status: 'open',
        noticeDate: '2026-05-02',
      },
    ],
    litigation: [],
    insurance: {
      hasMasterPolicy: true,
      carrier: 'Farmers Insurance',
      coverageNotes: 'Master policy covers common areas; no earthquake rider.',
    },
    extractedAt: '2026-07-20T00:00:00.000Z',
    source: 'mock-demand-statement.pdf',
  },
  hoaBoardMinutesFindings: {
    meetings: [
      {
        date: '2026-01-14',
        heldMeeting: true,
        directorsAbsent: [],
        vacantSeats: [],
        financials: { operatingBalance: 84210, reserveBalance: 261480 },
        notableApprovals: [{ description: 'Landscaping contract renewal', amount: 4200, fundSource: 'operating' }],
        complianceItems: [],
        signedAndApproved: true,
      },
      {
        date: '2026-02-01',
        heldMeeting: false,
        signedAndApproved: false,
      },
      {
        date: '2026-03-11',
        heldMeeting: true,
        directorsAbsent: ['Treasurer'],
        vacantSeats: [],
        financials: { operatingBalance: 79340, reserveBalance: 258900 },
        lienActions: [{ accountRef: 'Unit 14', action: 'authorized' }],
        notableApprovals: [],
        complianceItems: ['Reviewed delinquent account escalation policy'],
        signedAndApproved: true,
      },
      {
        date: '2026-04-01',
        heldMeeting: false,
        signedAndApproved: false,
      },
      {
        date: '2026-05-01',
        heldMeeting: false,
        signedAndApproved: false,
      },
      {
        date: '2026-06-09',
        heldMeeting: true,
        directorsAbsent: [],
        vacantSeats: ['At-Large Seat #2'],
        financials: { operatingBalance: 88760, reserveBalance: 249100 },
        notableApprovals: [{ description: 'Roof gutter repair', amount: 6800, fundSource: 'reserve' }],
        complianceItems: [],
        signedAndApproved: true,
      },
      {
        date: '2026-07-01',
        heldMeeting: false,
        signedAndApproved: false,
      },
      {
        date: '2026-08-05',
        heldMeeting: false,
        signedAndApproved: false,
      },
    ],
    extractedAt: '2026-08-01T00:00:00.000Z',
    source: 'mock-board-minutes.pdf',
  },
  hoaOperatingRulesFindings: {
    minimumLeaseTermDays: 30,
    smokingProhibited: true,
    petLimit: 2,
    fineScheduleSummary: '$100 first violation, $200 second, $500 third within 12 months',
    guestParkingLimitHours: 72,
    lienThresholdAmount: 1800,
    lienThresholdMonths: 12,
    lateChargePercent: 10,
    interestRatePercent: 12,
    designReviewFee: 135,
    notableRestrictions: ['No short-term rentals under 30 days', 'Exterior modifications require design review'],
    extractedAt: '2026-08-01T00:00:00.000Z',
    source: 'mock-operating-rules.pdf',
  },
  hoaAnnualBudgetFindings: {
    totalAnnualIncome: 1340000,
    totalAnnualExpense: 1265000,
    reserveAllocationAnnual: 210000,
    reservePercentFunded: 39.1,
    reserveProjection: [
      { fiscalYear: '2026', percentFunded: 39.1 },
      { fiscalYear: '2027', percentFunded: 31.6 },
      { fiscalYear: '2028', percentFunded: 25.4 },
      { fiscalYear: '2029', percentFunded: 30.2 },
      { fiscalYear: '2030', percentFunded: 38.7 },
    ],
    reserveDeficitPerUnit: 3150,
    specialAssessmentAnticipated: false,
    insurance: {
      hasMasterPolicy: true,
      carrier: 'Farmers Insurance',
      coverageNotes: 'Master policy covers common areas; no earthquake rider.',
      hasEarthquakeInsurance: false,
      hasFloodInsurance: false,
    },
    extractedAt: '2026-08-01T00:00:00.000Z',
    source: 'mock-annual-budget.pdf',
  },
};

const isHoaMockActive = (): boolean =>
  import.meta.env.DEV && new URLSearchParams(window.location.search).get('mock') === 'hoa';

export function useHOAChecklist(propertyId: string | undefined): UseHOAChecklistResult {
  const mockActive = isHoaMockActive();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['hoa-checklist', propertyId],
    queryFn: () => fetchHOAChecklist(propertyId ?? ''),
    enabled: !!propertyId && !mockActive,
    staleTime: 5 * 60 * 1000,
  });

  if (mockActive) {
    return { data: HOA_MOCK_RESPONSE, isLoading: false, isError: false };
  }

  if (isError) {
    logger.error('useHOAChecklist: failed to fetch HOA checklist', { propertyId, error });
  }

  return { data: data ?? null, isLoading, isError };
}
